import { ArrowRight, CheckCircle } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import useIsLoggedIn from "../../hooks/use-IsLoggedIn";
import Header from "../Profile/components/Header";
import Footer from "../StaticPages/Footer";

// Icons for the "How It Works" section
const ChooseJobIcon = () => (
	<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M5 10H27M5 10V24C5 25.1046 5.89543 26 7 26H25C26.1046 26 27 25.1046 27 24V10M5 10L7 6H25L27 10"
			stroke="#0A6FE2"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M12 15H20" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
		<path d="M12 20H16" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const UploadResumeIcon = () => (
	<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M6 6H26V26H6V6Z" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M6 6H16V16H6V6Z" fill="#F0F7FF" stroke="#0A6FE2" strokeWidth="2" />
		<path d="M10 11H12" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
		<path d="M10 15H22" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
		<path d="M10 19H18" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
		<path d="M10 23H22" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const RunFitCheckIcon = () => (
	<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M6 6H26V26H6V6Z" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<path d="M6 6H16V16H6V6Z" fill="#F0F7FF" stroke="#0A6FE2" strokeWidth="2" />
		<circle cx="11" cy="11" r="2" stroke="#0A6FE2" strokeWidth="2" />
		<circle cx="11" cy="21" r="2" stroke="#0A6FE2" strokeWidth="2" />
		<path d="M18 10H24" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
		<path d="M18 21H24" stroke="#0A6FE2" strokeWidth="2" strokeLinecap="round" />
	</svg>
);

// Curved arrow component
const CurvedArrow = ({ direction = "right" }) => (
	<svg
		width="120"
		height="20"
		viewBox="0 0 120 20"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		className={direction === "left" ? "transform scale-y-[-1]" : ""}
	>
		<path
			d="M2 18C20 18 30 2 60 2C90 2 100 18 118 18"
			stroke="#DEE2E6"
			strokeWidth="2"
			strokeLinecap="round"
			strokeDasharray="6 6"
		/>
		<path d="M112 14L118 18L112 22" stroke="#DEE2E6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

function FitCheckLandingPage() {
	const isLoggedIn = useIsLoggedIn();

	const benefits = [
		"Get instant insights.",
		"Know your gaps and how to improve.",
		"Get actionable next steps with decision guidance.",
		"Save time & apply efficiently.",
	];

	const steps = [
		{
			number: 1,
			title: "Choose a Job",
			description: "Pick from job listings or paste a job description.",
			icon: <ChooseJobIcon />,
		},
		{
			number: 2,
			title: "Upload/Select Resume",
			description: "Use your DSP resume, upload, or paste resume.",
			icon: <UploadResumeIcon />,
		},
		{
			number: 3,
			title: "Run FitCheck",
			description: "Get instant results with insights and action steps.",
			icon: <RunFitCheckIcon />,
		},
	];

	return (
		<>
			<Header isLoggedIn={isLoggedIn} />

			<main className="max-w-screen mx-auto">
				{/* Hero Section */}
				<section className="px-4 md:px-14 py-16">
					<div className="bg-primary-100 rounded-3xl relative overflow-hidden">
						<div className="flex flex-col items-center text-center pt-10 pb-96 md:pb-[500px] px-4">
							<div className="max-w-3xl mx-auto space-y-8">
								<h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 font-plus-jakarta leading-tight">
									Are You Ready for That Job? Don&apos;t Guess. FitCheck It
								</h1>
								<p className="text-lg md:text-xl lg:text-2xl text-gray-800 font-inter max-w-2xl mx-auto">
									FitCheckMe shows you how well you match a job, highlighting your strengths, gaps, and what to fix
									before you hit apply.
								</p>
								<div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
									<Link
										to="/fitchecks"
										className="bg-primary-700 hover:bg-primary-800 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-colors font-inter text-lg"
									>
										Try FitCheckMe
									</Link>
									<button
										type="button"
										className="border border-primary-400 text-primary-700 font-medium py-3 px-6 rounded-lg flex items-center gap-2 hover:bg-primary-50 transition-colors font-inter text-lg"
									>
										How it works
										<ArrowRight className="w-5 h-5" />
									</button>
								</div>
							</div>
						</div>
					</div>
					{/* Screenshot/Preview Area */}
					<div className="bg-white w-[80%] mx-auto p-20 pb-0 rounded-3xl top-[-400px] shadow-lg relative">
						<div className="relative">
							<img
								src="/images/home/fitcheck-preview.png"
								alt="FitCheck Dashboard Preview"
								className="w-full h-auto"
								onError={(e) => {
									e.target.style.display = "none";
									e.target.parentElement.innerHTML = `
											<div class="bg-gray-100 h-96 flex items-center justify-center">
												<div class="text-center text-gray-500">
													<p class="text-lg font-medium">FitCheck Dashboard Preview</p>
													<p class="text-sm mt-2">See your job match score instantly</p>
												</div>
											</div>
										`;
								}}
							/>
						</div>
					</div>
				</section>

				{/* Why FitCheck Section */}
				<section className="px-4 md:px-14 py-24 -mt-80 mx-auto max-w-screen-2xl">
					<div className="flex flex-col lg:flex-row gap-12 items-center">
						{/* Left: Image/Illustration */}
						<div className="lg:w-1/2">
							<div className="bg-white border border-gray-200 rounded-3xl p-8 relative overflow-hidden">
								<img src="/images/home/fitcheck-mockup.png" alt="" />
							</div>
						</div>

						{/* Right: Content */}
						<div className="lg:w-1/2 space-y-8">
							<div className="space-y-4">
								<h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-plus-jakarta">Why FitCheck?</h2>
								<p className="text-xl md:text-2xl text-gray-700 font-inter">
									Know exactly which jobs match your strength before you apply. Apply smarter, not harder.
								</p>
							</div>

							<ul className="space-y-4">
								{benefits.map((benefit, index) => (
									<li key={index} className="flex items-center gap-3">
										<CheckCircle className="w-5 h-5 text-primary-700 flex-shrink-0" />
										<span className="text-lg md:text-xl text-gray-600 font-inter">{benefit}</span>
									</li>
								))}
							</ul>

							<Link
								to="/fitchecks"
								className="inline-block bg-primary-700 hover:bg-primary-800 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-colors font-inter text-lg"
							>
								Get Started
							</Link>
						</div>
					</div>
				</section>

				{/* How It Works Section */}
				<section className="px-4 md:px-14 py-24 mx-auto max-w-screen-2xl">
					<div className="text-center mb-12">
						<h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-plus-jakarta mb-4">How It Works</h2>
						<p className="text-xl md:text-2xl text-gray-700 font-inter max-w-3xl mx-auto">
							Run a FitCheck in 3 Easy Steps. From picking a job to getting tailored insights in under a minute.
						</p>
					</div>

					{/* Steps */}
					<div className="relative max-w-5xl mx-auto">
						<div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
							{steps.map((step, index) => (
								<div key={step.number} className="relative">
									{/* Card */}
									<div className={`bg-white rounded-lg p-8 text-center relative ${index === 1 ? "md:mt-12" : ""}`}>
										{/* Step Number */}
										<span className="absolute top-4 right-6 text-6xl font-semibold text-gray-200 font-inter">
											{step.number}
										</span>

										{/* Icon */}
										<div className="bg-primary-100 w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6">
											{step.icon}
										</div>

										{/* Content */}
										<h3 className="text-xl md:text-2xl font-medium text-gray-900 font-inter mb-3">{step.title}</h3>
										<p className="text-lg md:text-xl text-gray-500 font-inter">{step.description}</p>
									</div>

									{/* Curved Arrow (hidden on last item and mobile) */}
									{index < steps.length - 1 && (
										<div className="hidden md:block absolute -right-8 top-20 z-10">
											<CurvedArrow direction={index === 0 ? "right" : "left"} />
										</div>
									)}
								</div>
							))}
						</div>
					</div>

					{/* CTA */}
					<div className="text-center mt-12 space-y-4">
						<p className="text-xl md:text-2xl text-gray-700 font-inter">That&apos;s it. Instant clarity.</p>
						<Link
							to="/fitchecks"
							className="inline-block bg-primary-700 hover:bg-primary-800 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-colors font-inter text-lg"
						>
							Try FitCheckMe Now
						</Link>
					</div>
				</section>

				{/* CTA Banner Section */}
				<section className="px-4 md:px-14 py-8 md:py-16">
					<div className="bg-gray-900 rounded-3xl overflow-hidden">
						<div className="relative">
							<img
								src="/images/home/fitcheck-banner.jpg"
								alt="FitCheck Banner"
								className="w-full h-80 md:h-96 object-cover opacity-50"
								onError={(e) => {
									e.target.style.display = "none";
								}}
							/>
							<div className="absolute inset-0 flex items-center justify-center p-8">
								<div className="text-center text-white max-w-3xl">
									<h2 className="text-3xl md:text-4xl font-bold font-plus-jakarta mb-4">
										Ready to find your perfect job match?
									</h2>
									<p className="text-lg md:text-xl text-gray-300 mb-6 font-inter">
										Stop guessing. Start applying with confidence. FitCheckMe helps you understand exactly where you
										stand.
									</p>
									<Link
										to="/fitchecks"
										className="inline-block bg-primary-700 hover:bg-primary-800 text-white font-bold py-3 px-8 rounded-lg shadow-md transition-colors font-inter text-lg"
									>
										Get Started for Free
									</Link>
								</div>
							</div>
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</>
	);
}

export default FitCheckLandingPage;
